import { defineStore } from 'pinia'
import type { ErrorType } from '~/composables/useErrorTracking'

export type SnackbarPayload = {
  text: string

  /**
   * Default: 8000
   */
  timeout?: number

  /**
   * Default: "notification"
   */
  color?: 'success' | 'error' | 'notification'

  /**
   * Default: `$i18n.t('close')`
   */
  closeText?: string

  /**
   * Stable i18n key for the error text (locale-independent).
   * Required when `color: 'error'` to fire a GA4 `error_message` event.
   * If omitted the snackbar shows but no tracking event is emitted.
   */
  textKey?: string

  /**
   * GA4 error type. Defaults to `'business'` when omitted and `color: 'error'`.
   */
  errorType?: ErrorType
}

export const useSnackbarStore = defineStore('snackbar', () => {
  const i18n = useNuxtApp().$i18n as unknown as ReturnType<typeof useI18n>
  const { trackError } = useErrorTracking()

  const isVisible = ref(false)
  const timeout = ref(8000)
  const text = ref('info')
  const color = ref('notification')
  const closeText = ref(i18n.t('close'))

  const showMessage = (payload: SnackbarPayload) => {
    timeout.value = payload.timeout ?? 8000
    text.value = payload.text
    color.value = payload.color ?? 'notification'
    isVisible.value = true
    closeText.value = payload.closeText ?? i18n.t('close')

    if (payload.color === 'error' && payload.textKey) {
      trackError({ type: payload.errorType ?? 'business', text: payload.textKey })
    }
  }

  return {
    isVisible,
    timeout,
    text,
    color,
    closeText,
    showMessage,
  }
})
